import * as d3 from 'd3'
import Chart from '../../ultils/Chart'
import emptyThumb from '../../assets/images/emptydata.svg'
import './styles.css'
import d3tip from '../../ultils/d3-tip'
import {
  countYAxisTick,
  formatPercentageNumber,
  getChartColorRange,
} from '../../ultils/chart-d3-utils'

class StackedAreaChart extends Chart {
  init({ data, relations, setSelection, stackKeys, inModal, chartId }) {
    let vis = this
    vis.data = data
    vis.relations = relations
    vis.setSelection = setSelection
    vis.parseTime = d3.timeParse('%d/%m/%Y')
    vis.formatTime = d3.timeFormat('%d/%m/%Y')
    vis.bisectDate = d3.bisector(d => d.date).left
    vis.inModal = inModal
    vis.chartId = chartId

    vis.svg
      .append('defs')
      .append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('x', 0)
      .attr('width', vis.WIDTH)
      .attr('height', vis.HEIGHT)

    vis.g
      .append('path')
      .attr('class', 'line')
      .attr('fill', 'none')
      .attr('clip-path', 'url(#clip)')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.x = d3.scaleTime()
    vis.y = d3.scaleLinear()

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(vis.inModal ? 8 : 2)
      .tickSizeOuter(0)
    vis.yAxisCall = d3
      .axisLeft()
      .ticks(inModal ? 6 : 4)
      .tickSizeOuter(0)

    vis.xAxis = vis.g
      .append('g')
      .attr('class', 'x axisStackedAreaChart')
      .attr('transform', `translate(0, ${vis.HEIGHT})`)
    vis.yAxis = vis.g.append('g').attr('class', 'y axisStackedAreaChart')

    vis.areaGroup = vis.g.append('g')

    vis.focus = vis.g
      .append('g')
      .attr('class', 'focus')
      .style('display', 'none')

    vis.focus
      .append('line')
      .attr('class', 'x-hover-line-stack-area hover-line')
      .attr('y1', 0)
      .attr('y2', vis.HEIGHT)

    vis.yLegend = vis.svg
      .append('text')
      .attr('class', inModal ? 'axis-label label-in-modal' : 'axis-label')
      .attr('text-anchor', 'end')
      .attr('x', 30)
      .attr('y', 15)

    vis.update({ data, stackKeys })
    vis.createTooltip()
  }

  createTooltip() {
    const vis = this
    const inModal = vis.inModal ? '-inModal' : '-notInModal'
    d3.selectAll(`.stackarea-chart${vis.chartId}${inModal}`).remove()

    vis.tip = d3tip()
      .attr(
        'class',
        `d3-tip-stack-area-chart stackarea-chart${vis.chartId}${inModal}`,
      )
      .html((e, d) => vis.generateTooltipHtml(d))

    vis.g.call(vis.tip)
  }

  formatAbbreviation(x) {
    const formatSi = d3.format('.3s')
    const s = formatSi(x)
    switch (s[s.length - 1]) {
      case 'G':
        return `${s.slice(0, -1)}B` // billions
      case 'k':
        return `${s.slice(0, -1)}K` // thousands
      case 'M':
        return `${s.slice(0, -1)}M` // thousands
    }
    return s
  }

  update({ data, showComposition, stackKeys }) {
    let vis = this
    vis.data = data || vis.data
    vis.showComposition = showComposition == null ? false : showComposition
    vis.stackKeys = stackKeys || vis.stackKeys
    vis.t = () => d3.transition().duration(750)
    vis.svg.selectAll('image').remove()

    vis.colorRange = getChartColorRange(vis.stackKeys.length)
    vis.color = d3.scaleOrdinal().range(vis.colorRange)
    const keys = []
    vis.stackKeys.reverse().forEach(stackKey => {
      keys.push(stackKey.key)
    })
    vis.keys = keys
    vis.stackKeys = vis.stackKeys.reverse()
    vis.stack = d3.stack().keys(keys)

    vis.g.selectAll('.stackarea-circle').remove()
    for (let i = 0; i < vis.keys.length; i++) {
      vis.focus
        .append('circle')
        .attr('class', `${vis.keys[i]}-circle stackarea-circle`)
        .attr('r', 4)
        .attr('fill', vis.colorRange[i])
        .attr('stroke', '#ffffff')
        .attr('stroke-width', '1px')
    }

    if (vis.data.length <= 1) {
      // treat 1 record as empty state to clear xAxis & yAxis value
      vis.data = []
      vis.svg
        .append('svg:image')
        .attr('xlink:href', emptyThumb)
        .attr('x', vis.WIDTH / 2 - 77 + vis.MARGIN.LEFT)
        .attr('y', vis.HEIGHT / 2 - 37 + vis.MARGIN.TOP)
        .attr('width', '155')
        .attr('height', '73')
      vis.yLegend.text('')
    } else {
      showComposition ? vis.yLegend.text('%') : vis.yLegend.text('')
    }

    vis.transformedData = showComposition
      ? vis.data.map(d => {
          const total = vis.stackKeys.reduce(
            (acc, curr) => acc + Number(d[curr.key]),
            0,
          )
          const newVals = vis.stackKeys.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.key]: total === 0 ? 0 : Number(d[curr.key]) / total,
            }),
            {},
          )
          return {
            ...d,
            ...newVals,
            total,
          }
        })
      : vis.data

    vis.x
      .range([0, vis.WIDTH])
      .domain(d3.extent(vis.transformedData, d => d.date))
    vis.y
      .range([vis.HEIGHT, 0])
      .domain([
        0,
        d3.max(vis.transformedData, d =>
          showComposition
            ? 1
            : d3.sum(Object.keys(d).map(key => (key !== 'date' ? d[key] : 0))),
        ),
      ])

    vis.yAxisCall.tickFormat(x =>
      showComposition ? (x * 100).toFixed(0) : vis.formatAbbreviation(x),
    )

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(countYAxisTick(vis.data))
      .tickSizeOuter(0)

    vis.xAxisCall.scale(vis.x)
    vis.xAxis.transition(vis.t()).call(vis.xAxisCall)
    vis.yAxisCall.scale(vis.y)
    vis.yAxis.transition(vis.t()).call(vis.yAxisCall)

    vis.color.domain(vis.stack(vis.transformedData))
    vis.g.selectAll('.area').remove()

    vis.area = d3
      .area()
      .curve(d3.curveCatmullRom)
      .x(d => vis.x(d.data.date))
      .y0(d => vis.y(d[0]))
      .y1(d => vis.y(d[1]))

    vis.areas = vis.areaGroup
      .selectAll('.area')
      .data(vis.stack(vis.transformedData), d => d.key)
      .join(
        enter => {
          return enter
            .append('path')
            .attr('class', 'area')
            .style('fill', d => vis.color(d.key))
            .attr('d', vis.area)
            .style('stroke', '#333333')
            .style('stroke-width', '0.5px')
        },
        update =>
          update.call(update => update.transition(vis.t()).attr('d', vis.area)),
        exit => exit.remove(),
      )

    vis.svg
      .on('mousemove', e => vis.mousemove(e))
      .on('mouseover', () => {
        vis.tip.hide()
        vis.focus.style('display', null)
        !vis.inModal && vis.setSelection(vis.relations)
      })
      .on('mouseout', () => {
        vis.tip.hide()
        vis.focus.style('display', 'none')
        // d3.selectAll('.d3-tip-stack-area-chart').remove()
        !vis.inModal && vis.setSelection(null)
      })
  }

  formatNumber(number) {
    const vis = this
    const x = vis.formatAbbreviation(number)
    return !isNaN(number) ? `${x}` : 'N/A'
  }

  generateTooltipHtml(data) {
    const vis = this
    const { length } = vis.stackKeys
    let returnDiv = `<div class="tip-content">`
    let total = 0
    for (let i = 0; i < length; i++) {
      total += data[vis.keys[length - i - 1]]
      returnDiv += `
        <div class="row">
          <div class="line-chart-row-title">
            <span class="dot" style="background-color:${
              vis.colorRange[length - i - 1]
            };"></span> ${vis.stackKeys[i].title}: 
          </div>
          <div class="line-chart-row-content">
          ${
            vis.showComposition
              ? `${formatPercentageNumber(data[vis.keys[length - i - 1]])} %`
              : vis.formatNumber(data[vis.keys[length - i - 1]])
          }</div>
        </div>
      `
    }
    if (!vis.showComposition) {
      returnDiv += `
      <div class="row">
        <div class="line-chart-row-title">
            Total: 
        </div>
        <div class="line-chart-row-content">
           ${vis.formatNumber(total)}
        </div>
      </div>
    `
    }
    returnDiv += '<div/><div/><div/>'
    return returnDiv
  }

  calculateSumArrays(inputArray) {
    const totalArray = []
    totalArray.push(!isNaN(inputArray[0]) ? Number(inputArray[0]) : 0)
    for (let i = 1; i < inputArray.length; i++) {
      let count = 0
      for (let j = 0; j <= i; j++) {
        count += !isNaN(inputArray[j]) ? Number(inputArray[j]) : 0
      }
      totalArray.push(count)
    }
    return totalArray
  }

  mousemove(e) {
    let vis = this
    if (vis.transformedData.length > 0) {
      const x0 = vis.x.invert(d3.pointer(e)[0] - vis.MARGIN.LEFT)
      const i = vis.bisectDate(
        vis.transformedData,
        x0,
        1,
        vis.transformedData.length - 1,
      )
      const d0 = vis.transformedData[i - 1]
      const d1 = vis.transformedData[i]
      const d = x0 - d0.date > d1.date - x0 ? d1 : d0

      vis.focus
        .select('.x-hover-line-stack-area')
        .attr('x1', vis.x(d.date))
        .attr('x2', vis.x(d.date))
      const temp = []
      vis.keys.forEach(key => temp.push(d[key]))
      const circleCyValues = vis.calculateSumArrays(temp)
      for (let j = 0; j < vis.keys.length; j++) {
        vis.focus
          .select(`.${vis.keys[j]}-circle`)
          .attr('cx', vis.x(d.date))
          .attr('cy', vis.y(circleCyValues[j]))
      }

      vis.tip.show(e, d, 10, -d3.pointer(e)[1] - 40)
    }
  }
}

export default StackedAreaChart
